import React from 'react';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { switchProp } from 'src/shared/styled-utils/switchProp';
import styled, { css } from 'styled-components';
import { BoxShadow, Colors, Font, FontSize, LineHeight } from 'web-common/src/shared/styles';

/**
 * Image icon used in the `Chip` component.
 * Use this when the icon is an image and has a src.
 */
export const ChipImageIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 6px;
  margin-bottom: 2px;
`;

export enum ChipColor {
  RED = 'RED',
  LIGHT_RED = 'LIGHT_RED',
  LIGHT_LIGHT_RED = 'LIGHT_LIGHT_RED',
  WHITE = 'WHITE',
  YELLOW = 'YELLOW',
  LIGHT_YELLOW = 'LIGHT_YELLOW',
  LIGHT_LIGHT_YELLOW = 'LIGHT_LIGHT_YELLOW',
  LIGHT_GREEN = 'LIGHT_GREEN',
  DARK_PURPLE = 'DARK_PURPLE',
  LIGHT_GRAY = 'LIGHT_GRAY',
  KOHLRABI = 'KOHLRABI',
  EGGPLANT = 'EGGPLANT',
}

const ChipContainer = styled.div<{ chipColor: ChipColor; showShadow: boolean }>`
  border-radius: 4px;
  box-shadow: ${ifProp('showShadow', BoxShadow.CHIP, 0)};
  display: inline-flex;
  font-family: ${Font.Family.SANS};
  font-size: ${FontSize.XS};
  height: ${LineHeight.S};
  line-height: ${LineHeight.S};
  padding: 0 8px;
  align-items: center;

  ${switchProp('chipColor', {
    [ChipColor.RED]: css`
      background-color: ${Colors.RADISH};
      color: ${Colors.WHITE};
    `,
    [ChipColor.WHITE]: css`
      background-color: ${Colors.WHITE};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.YELLOW]: css`
      background-color: ${Colors.JACKFRUIT};
      color: ${Colors.BLACK};
    `,
    [ChipColor.LIGHT_YELLOW]: css`
      background-color: ${Colors.JACKFRUIT_LITE_10};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.LIGHT_LIGHT_YELLOW]: css`
      background-color: ${Colors.JACKFRUIT_LITE_20};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.LIGHT_RED]: css`
      background-color: ${Colors.BELL_PEPPER_LITE_20};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.LIGHT_LIGHT_RED]: css`
      background-color: ${Colors.BELL_PEPPER_LITE_30};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.LIGHT_GREEN]: css`
      background-color: ${Colors.KALE_LITE_20};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.LIGHT_GRAY]: css`
      background-color: ${Colors.GRAY_LITE_30};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.DARK_PURPLE]: css`
      background-color: ${Colors.PLUM_DARK_10};
      color: ${Colors.WHITE};
    `,
    [ChipColor.KOHLRABI]: css`
      background-color: ${Colors.KOHLRABI_LITE_30};
      color: ${Colors.GRAY_DARK_30};
    `,
    [ChipColor.EGGPLANT]: css`
      background-color: ${Colors.EGGPLANT};
      color: ${Colors.WHITE};
    `,
  })}
`;

const ChipText = styled.div`
  display: flex;
  align-items: center;
`;

interface ChipProps {
  className?: string;
  color?: ChipColor;
  icon?: React.ReactNode; // Use one of the icon components above to keep consistent with size and positioning.
  onClick?: () => void;
  showShadow?: boolean;
  text: string;
  dataCy?: string;
}

export const Chip: React.FC<ChipProps> = ({
  className,
  icon,
  text,
  color = ChipColor.WHITE,
  onClick,
  showShadow = true,
  dataCy = '',
}) => (
  <ChipContainer className={className} chipColor={color} onClick={onClick} showShadow={showShadow}>
    <ChipText>
      {icon ?? null}
      <span data-cy={dataCy}>{text}</span>
    </ChipText>
  </ChipContainer>
);
